import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function ChangePassword() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) { setErr("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setErr("New passwords do not match."); return; }
    if (next === "changeme123") { setErr("Please choose something other than the default."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      await refreshUser();
      navigate("/");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed to change password");
    } finally { setSubmitting(false); }
  };

  const mustChange = user?.must_change_password;

  return (
    <div className="min-h-screen bg-sand paper-grain flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 text-espresso mb-6">
          <ShieldCheck className="h-5 w-5" />
          <span className="label-caps">{mustChange ? "First-time setup" : "Account security"}</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          Set a <span className="font-script italic">new</span> password
        </h1>
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
          {mustChange
            ? "For security, please replace the shared default password before continuing."
            : "Update the password on your account."}
        </p>

        <Card className="mt-7 border-border/70 bg-card">
          <CardContent className="p-6 sm:p-7">
            <form onSubmit={submit} className="space-y-5" data-testid="change-password-form">
              <div>
                <Label className="label-caps">Current password</Label>
                <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                  required data-testid="cp-current" className="mt-2 bg-background border-border/70 focus-visible:ring-espresso" />
              </div>
              <div>
                <Label className="label-caps">New password</Label>
                <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                  required minLength={8} data-testid="cp-new" className="mt-2 bg-background border-border/70 focus-visible:ring-espresso" />
                <p className="text-xs text-muted-foreground mt-2">Minimum 8 characters, and different from the default.</p>
              </div>
              <div>
                <Label className="label-caps">Confirm new password</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  required data-testid="cp-confirm" className="mt-2 bg-background border-border/70 focus-visible:ring-espresso" />
              </div>

              {err && <div className="text-sm text-destructive" data-testid="cp-error">{err}</div>}

              <button type="submit" disabled={submitting} data-testid="cp-submit"
                className="w-full inline-flex items-center justify-center gap-2 border border-espresso bg-espresso text-sand px-6 py-3 text-[11px] uppercase tracking-[0.28em] font-medium hover:bg-espresso/90 transition-colors disabled:opacity-60">
                {submitting ? "Updating…" : "Update password"}
                <span aria-hidden>→</span>
              </button>

              {mustChange && (
                <button
                  type="button"
                  onClick={async () => { await logout(); navigate("/login"); }}
                  className="w-full text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
                  data-testid="cp-logout"
                >
                  Sign out instead
                </button>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
